import {
  GenerateRebalancingPlanCombinedParams,
  RebalancingSchedule,
} from '@cast/types';
import { CreateKentRebalancingScheduleData } from '@cast/types-kent';
import { EvictorConfig } from '@cast/types-workload-eviction';

import { ApplyStep, AutoOptimizationScreen } from './_types';
import {
  EnvironmentType,
  OptimizationGoal,
} from './open-api-types/autopilot-types';
import {
  DEFAULT_AGGRESSIVE_MODE_CONFIG,
  DEFAULT_KENT_AGGRESSIVE_MODE_CONFIG,
} from '@cast/split-me/features/cluster/rebalance';

type CardOption<T extends string = string> = {
  value: T;
  title: string;
  description: string;
};

export const PRIORITY_OPTIONS: CardOption<OptimizationGoal>[] = [
  {
    value: 'COST_SAVING',
    title: 'Cost saving',
    description:
      'Tightest resource quantiles, smallest headroom. Lower margins, higher savings.',
  },
  {
    value: 'BALANCED',
    title: 'Balanced',
    description:
      'Reasonable savings while staying conservative on critical workloads.',
  },
  {
    value: 'PERFORMANCE',
    title: 'Performance',
    description:
      'Wider headroom, higher quantiles, larger apply thresholds. Smaller savings, fewer restarts.',
  },
];

export const ENVIRONMENT_OPTIONS: CardOption<EnvironmentType>[] = [
  {
    value: 'MOSTLY_PRODUCTION',
    title: 'Mostly production',
    description:
      'Production with some staging or batch namespaces. OpsPilot defers risky changes with graceful restart.',
  },
  {
    value: 'MIXED',
    title: 'Mixed prod / non-prod',
    description:
      'Mix of environments split by namespace. OpsPilot rightsizes non-prod more aggressively.',
  },
  {
    value: 'MOSTLY_NON_PRODUCTION',
    title: 'Mostly non-production',
    description:
      'Dev / staging with some production namespaces. Aggressive rightsizing, applied immediately.',
  },
  {
    value: 'NON_PRODUCTION',
    title: 'Non-production',
    description:
      'Dev / staging / preview only. Maximum rightsizing with no production constraints.',
  },
];

export const DEFAULT_EXCLUDED_NAMESPACES = [
  'castai-agent',
  'kube-system',
] as const;

export const APPLY_STEP_DESCRIPTIONS: Record<ApplyStep, string> = {
  create: 'Spinning up fresh scaling policies tailored to each workload.',
  update: 'Realigning existing policies with the recommended targets.',
  order: 'Reordering the playbook so the right policy fires first.',
};

export const LOADING_SCREENS: AutoOptimizationScreen[] = [
  AutoOptimizationScreen.Applying,
];

export const STEP_INDEX_TO_SCREEN = [
  AutoOptimizationScreen.Features,
  AutoOptimizationScreen.Priority,
  AutoOptimizationScreen.Exclusions,
  AutoOptimizationScreen.Recommendations,
  AutoOptimizationScreen.Completed,
] as const;

/* 
Profiles source:
Kent: https://castai.atlassian.net/wiki/spaces/cast/pages/4672881301/KENT+Scheduled+rebalancer+profiles
Autoscaler: https://castai.atlassian.net/wiki/spaces/cast/pages/4346740783/Optimize+Now+Button+Design+Doc#Appendix%3A-Node-autoscaler-profile-defaults
*/
export const OPTIMIZATION_GOAL_TO_GENERATE_AUTOSCALER_REBALANCER_PLAN_PARAMS: Record<
  OptimizationGoal,
  GenerateRebalancingPlanCombinedParams
> = {
  PERFORMANCE: {
    evictGracefully: true,
    keepDrainTimeoutNodes: false,
    aggressiveMode: false,
    executionConditions: { enabled: true, achievedSavingsPercentage: 15 },
  },
  BALANCED: {
    evictGracefully: true,
    keepDrainTimeoutNodes: false,
    aggressiveMode: false,
    executionConditions: { enabled: true, achievedSavingsPercentage: 10 },
  },
  COST_SAVING: {
    evictGracefully: false,
    keepDrainTimeoutNodes: null,
    aggressiveMode: true,
    aggressiveModeConfig: DEFAULT_AGGRESSIVE_MODE_CONFIG,
    executionConditions: { enabled: true, achievedSavingsPercentage: 0 },
  },
};

export const OPTIMIZATION_GOAL_TO_GENERATE_KENT_REBALANCER_PLAN_PARAMS: Record<
  OptimizationGoal,
  GenerateRebalancingPlanCombinedParams
> = {
  PERFORMANCE: {
    aggressiveMode: false,
  },
  BALANCED: {
    aggressiveMode: false,
  },
  COST_SAVING: {
    aggressiveMode: true,
    kentAggressiveModeConfig: DEFAULT_KENT_AGGRESSIVE_MODE_CONFIG,
  },
};

export const OPTIMIZATION_GOAL_TO_EMPTY_NODE_TTL_SECONDS: Record<
  OptimizationGoal,
  number
> = {
  PERFORMANCE: 900, // 15 min
  BALANCED: 300, // 5 min
  COST_SAVING: 0, // 0 min
};

export const OPTIMIZATION_GOAL_TO_SCHEDULE_PARAMS: Record<
  OptimizationGoal,
  Pick<RebalancingSchedule, 'launchConfiguration' | 'triggerConditions'>
> = {
  PERFORMANCE: {
    launchConfiguration: {
      rebalancingOptions: {
        evictGracefully: true,
        drainFailureConfig: { disableUncordon: false },
        executionConditions: { enabled: true, achievedSavingsPercentage: 15 },
        maxSimultaneousDrains: 2,
      },
    },
    triggerConditions: { ignoreSavings: false, savingsPercentage: 15 },
  },
  BALANCED: {
    launchConfiguration: {
      rebalancingOptions: {
        evictGracefully: true,
        drainFailureConfig: { disableUncordon: false },
        executionConditions: { enabled: true, achievedSavingsPercentage: 10 },
        maxSimultaneousDrains: 10,
      },
    },
    triggerConditions: { ignoreSavings: false, savingsPercentage: 10 },
  },
  COST_SAVING: {
    launchConfiguration: {
      rebalancingOptions: {
        evictGracefully: false,
        executionConditions: { enabled: true, achievedSavingsPercentage: 0 },
        aggressiveModeConfig: DEFAULT_AGGRESSIVE_MODE_CONFIG,
      },
    },
    triggerConditions: { ignoreSavings: false, savingsPercentage: 0 },
  },
};

export const OPTIMIZATION_GOAL_TO_KENT_SCHEDULE_PARAMS: Record<
  OptimizationGoal,
  Pick<
    CreateKentRebalancingScheduleData,
    'launchConfiguration' | 'triggerConditions'
  >
> = {
  PERFORMANCE: {
    launchConfiguration: {
      nodeTtlSeconds: 900,
      numTargetedNodes: 0,
      rebalancingOptions: {
        executionConditions: { enabled: true, achievedSavingsPercentage: 15 },
        // Kent schedule updates deep-merge, so omitting aggressiveModeConfig would
        // keep the flags persisted by the cost-saving preset. An explicit empty
        // object resets every flag to its default (aggressive mode off).
        aggressiveModeConfig: {},
      },
    },
    triggerConditions: { ignoreSavings: false, savingsPercentage: 15 },
  },
  BALANCED: {
    launchConfiguration: {
      nodeTtlSeconds: 300,
      numTargetedNodes: 0,
      rebalancingOptions: {
        executionConditions: { enabled: true, achievedSavingsPercentage: 10 },
        // Kent schedule updates deep-merge, so omitting aggressiveModeConfig would
        // keep the flags persisted by the cost-saving preset. An explicit empty
        // object resets every flag to its default (aggressive mode off).
        aggressiveModeConfig: {},
      },
    },
    triggerConditions: { ignoreSavings: false, savingsPercentage: 10 },
  },
  COST_SAVING: {
    launchConfiguration: {
      nodeTtlSeconds: 60,
      numTargetedNodes: 0,
      rebalancingOptions: {
        aggressiveModeConfig: DEFAULT_KENT_AGGRESSIVE_MODE_CONFIG,
        executionConditions: { enabled: true, achievedSavingsPercentage: 5 },
      },
    },
    triggerConditions: { ignoreSavings: false, savingsPercentage: 5 },
  },
};

export const OPTIMIZATION_GOAL_TO_AUTOSCALER_EVICTOR_CONFIG = {
  PERFORMANCE: {
    cycleInterval: '900s',
    maxTargetNodesPerCycle: 5,
    scopedMode: true,
    aggressiveMode: false,
    nodeGracePeriodMinutes: 15,
    ignorePodDisruptionBudgets: false,
  },
  BALANCED: {
    cycleInterval: '300s',
    maxTargetNodesPerCycle: 20,
    scopedMode: false,
    aggressiveMode: false,
    nodeGracePeriodMinutes: 5,
    ignorePodDisruptionBudgets: false,
  },
  COST_SAVING: {
    cycleInterval: '60s',
    maxTargetNodesPerCycle: 40,
    scopedMode: false,
    aggressiveMode: true,
    nodeGracePeriodMinutes: 1,
    ignorePodDisruptionBudgets: false,
  },
} satisfies Record<OptimizationGoal, EvictorConfig>;

// 5-step wizard: Features (0) → Profile (1) → Scope (2) → Review (3) → Done (4).
// Applying and Completed share step index 4 so the stepper advances when
// the user clicks Optimize on the Recommendations screen.
export const SCREEN_TO_STEP_INDEX: Record<AutoOptimizationScreen, number> = {
  [AutoOptimizationScreen.Features]: 0,
  [AutoOptimizationScreen.Priority]: 1,
  [AutoOptimizationScreen.Exclusions]: 2,
  [AutoOptimizationScreen.Recommendations]: 3,
  [AutoOptimizationScreen.Applying]: 4,
  [AutoOptimizationScreen.Completed]: 4,
};

export const DEFAULT_REBALANCING_SCHEDULE_CRON = '0 0 * * 1';
export const DEFAULT_REBALANCING_SCHEDULE_TIME_ZONE = 'UTC';
// Kent schedules are UTC-only.
export const KENT_REBALANCING_SCHEDULE_TIME_ZONE = 'UTC';
